import React from 'react'
import PropTypes from 'prop-types'
import {findDOMNode} from "react-dom"

import UI5CalendarMonthHeader from "./month-selection-header"
import VScroll from "../../../scrolling/custom-v-scroll/v-scroll"
import MonthSelectionItem from "./month-selection-item"

import Moment from 'moment'

const YEARS_BEFORE_AND_AFTER    = 10 // number of years added to start and end of selection range
const ADD_YEARS_NUMBER          = 10 // how many are added on scroll reach start or end

const debug = false
// const debug = "UI5CalendarMonthSelection"

export default class UI5CalendarMonthSelection extends React.Component {
    static propTypes = {
        type            : PropTypes.oneOf(['single', 'range']),

        initialMonth    : PropTypes.object,
        selectedDays    : PropTypes.arrayOf(PropTypes.shape({
            startDate   : PropTypes.object,                     //Moment
            endDate     : PropTypes.object,                     //Moment
            className   : PropTypes.string
        })),

        onBack          : PropTypes.func,
        onSelectMonth   : PropTypes.func,
    }

    static defaultProps = {
        type            : 'single',
        initialMonth    : Moment()
    }

    constructor(props) {
        super(props)

        // collect all months where days are selected
        this.monthsWithSelectedDays = {} // map with 'YYYY-MM' key format
        _.map(props.selectedDays, selectedData=>{
            const startMoment   = selectedData.startDate.clone()
            const endMoment     = selectedData.endDate ? selectedData.endDate.clone() : selectedData.startDate.clone()
            while (startMoment.isSameOrBefore(endMoment, 'month')) {
                this.monthsWithSelectedDays[startMoment.format('YYYY-MM')] = true
                startMoment.add(1, 'month')
            }
        })

        // collect initial years for whole initially available range of years
        this.years = []
        const currentYear = Moment().startOf('year')
        const initialYear = props.initialMonth.clone().startOf('year')

        const startYear = Moment(_.min([currentYear.format("YYYY"), initialYear.format("YYYY")])).subtract(YEARS_BEFORE_AND_AFTER,'years')
        const endYear   = Moment(_.max([currentYear.format("YYYY"), initialYear.format("YYYY")])).add(YEARS_BEFORE_AND_AFTER,'years')

        const moment = startYear.clone()
        while(moment.isSameOrBefore(endYear)) {
            this.years.push(this._initYear(moment))
            moment.add(1, 'year')
        }

        debug && console.log(debug, "constructor", this.monthsWithSelectedDays, this.years)
    }

    componentDidMount() {
        this.scrollToMonth(this.selectedMonthRef)
    }

    _initYear = (moment) => {
        const today = Moment()
        const year = {
            moment: moment.clone(),
            months: []
        }

        for (let j=0; j<12; j++) {
            const monthMoment = moment.clone().add(j, 'months')
            year.months.push({
                moment              : monthMoment,
                isSelected          : this.props.initialMonth.isSame(monthMoment, 'month'),                                     // check if month is selected in view
                isHighlighted       : this.monthsWithSelectedDays[monthMoment.format('YYYY-MM')],                               // check if current month has selected days
                isHighlightedStart  : !this.monthsWithSelectedDays[monthMoment.clone().subtract(1, 'month').format('YYYY-MM')], // check if previous month has selected days
                isHighlightedEnd    : !this.monthsWithSelectedDays[monthMoment.clone().add(1, 'month').format('YYYY-MM')],      // check if next month has selected days
                isCurrent           : today.isSame(monthMoment, 'month'),                                              // check if this month is today month
            })
        }

        return year
    }

    scrollToMonth = (ref, duration = 0) => {
        const monthNode = findDOMNode(ref)

        debug && console.log(debug, "scrollToMonth", monthNode)

        if (monthNode) {
            debug && console.log(debug, "scrollToMonth monthNode.offsetTop", monthNode.offsetTop)
            const scrollTop = monthNode.offsetTop
            this.scroll.setScrollTop(scrollTop, true, duration)
        }
    }

    onScrollStartPreReach = () => {
        if (!this.scroll || this.startPreReachProcessing) return

        this.startPreReachProcessing = true             // flag to prevent multiple calls of onScrollStartPreReach at the same time
        const firstYear = this.years[0].moment.clone()  // get year to add new ones before it

        debug && console.log(debug, "onScrollStartPreReach", firstYear)

        // add required number of years with their months
        for(let i=0; i < ADD_YEARS_NUMBER; i++) {
            firstYear.subtract(1, 'year')
            this.years.unshift(this._initYear(firstYear))
        }

        // get current scroll content height
        const scrollNode = findDOMNode(this.scrollContent)
        const scrollHeight = scrollNode.offsetHeight

        this.forceUpdate(()=>{
            // compare previous scroll content height with new one
            const heightDiff = scrollNode.offsetHeight - scrollHeight
            debug && console.log(debug, "onScrollStartPreReach scrollTop", scrollNode.offsetHeight, heightDiff)

            // update scroll top by the value of added height to content
            if (heightDiff > 0) this.scroll.setScrollTop(heightDiff, true, 0)

            this.startPreReachProcessing = false
        })
    }

    onScrollEndPreReach = () => {
        const lastYear = this.years[this.years.length - 1].moment.clone() // get year to add new ones after it

        debug && console.log(debug, "onScrollEndPreReach", lastYear)

        // add required number of years with their months
        for(let i=0; i < ADD_YEARS_NUMBER; i++) {
            lastYear.add(1, 'year')
            this.years.push(this._initYear(lastYear))
        }

        this.forceUpdate()
    }

    onSelectMonth = (monthMoment) => {
        debug && console.log(debug, "onSelectMonth", monthMoment)
        this.props.onSelectMonth(monthMoment)
    }

    render() {
        return (
            <div className={Classes("calendar-content", this.props.className)}>
                <UI5CalendarMonthHeader onBack      = {this.props.onBack}
                                        onCurrent   = {()=>this.scrollToMonth(this.currentMonthRef, 500)}/>

                <div className={Classes("month-selection-separator", {range: this.props.type === 'range'})}/>

                <VScroll ref                = {r=>this.scroll = r}
                         className          = {Classes("month-selection", {range: this.props.type === 'range'})}
                         scrollTop          = {1000}
                         minDragHeight      = {30}
                         onStartPreReach    = {this.onScrollStartPreReach}
                         onEndPreReach      = {this.onScrollEndPreReach}>
                    <div ref={r=>this.scrollContent = r} className="month-selection-content">
                        {_.map(this.years, year=>(
                            <div key={year.moment.format("x")} className="month-selection-year">
                                <div className="year-title">{year.moment.format("YYYY")}</div>
                                <div className="year-months">
                                    {_.map(year.months, month=> (
                                        <MonthSelectionItem ref      = {r=>{
                                            if (month.isSelected)   this.selectedMonthRef = r
                                            if (month.isCurrent)    this.currentMonthRef = r
                                        }}
                                                            key      = {month.moment.format("x")}
                                                            data     = {month}
                                                            onClick  = {this.onSelectMonth}/>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </VScroll>
            </div>
        )
    }
}